import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of, tap, timer, switchMap } from 'rxjs';
import { openDB, IDBPDatabase } from 'idb';
import { environment } from '@ui-nuvertos/shared';
import { authConfig } from './auth.config';
import { PermissionService } from './permission.service';

interface AuthDB {
  'auth-store': {
    key: string;
    value: any;
  };
}

export interface User {
  id: string;
  email: string;
  token: string;
  refreshToken: string;
  expiresAt: number;
  center_id?: number;
  role?: string;
}

export interface CenterInfo {
  id: number;
  code: string;
  name: string;
  location?: string;
  description?: string;
  is_active: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();
  private sessionTimeout: number = 30 * 60 * 1000; // 30 minutes
  private refreshTimer?: any;

  private dbPromise: Promise<IDBPDatabase<AuthDB>>;
  private http = inject(HttpClient);
  private permissionService = inject(PermissionService);
  
  constructor() {
    this.dbPromise = openDB<AuthDB>('auth-db', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('auth-store')) {
          db.createObjectStore('auth-store');
        }
      }
    });
    
    this.initializeSession();
  }

  private initializeSession(): void {
    this.getAuthData('user').then(user => {
      if (user && this.isTokenValid(user)) {
        this.currentUserSubject.next(user);
        this.startSessionTimer();
      } else {
        this.clearAuthData();
      }
    });
  }

  private isTokenValid(user: User): boolean {
    return user.expiresAt > Date.now();
  }

  private startSessionTimer(): void {
    // Only start timer if frontend session timeout is enabled
    if (!authConfig.enableSessionTimeout) {
      return;
    }
    
    this.refreshTimer = timer(this.sessionTimeout).pipe(
      switchMap(() => this.refreshToken())
    ).subscribe({
      next: (response) => {
        if (response) {
          this.startSessionTimer(); // Restart timer
        } else {
          this.logout().subscribe();
        }
      },
      error: () => {
        this.logout().subscribe();
      }
    });
  }

  loadUserSession(): Observable<User | null> {
    return this.http.get<User>(`${this.apiUrl}/auth/authenticate`, { withCredentials: true }).pipe(
      tap(user => {
        if (user) {
          this.currentUserSubject.next(user);
          this.saveAuthData('user', user);
          this.startSessionTimer();
        }
      }),
      catchError(() => {
        this.currentUserSubject.next(null);
        return of(null);
      })
    );
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/login`, { email, password }, { withCredentials: true })
      .pipe(
        tap((response) => {
          if (response.user) {
            const user: User = {
              ...response.user,
              expiresAt: Date.now() + (response.expiresIn * 1000)
            };
            this.saveAuthData('user', user);
            this.currentUserSubject.next(user);
            this.startSessionTimer();
          }
        })  
      );
  }

  refreshToken(): Observable<any> {
    const user = this.currentUserSubject.value;
    if (!user?.refreshToken) {
      return of(null);
    }

    return this.http.post<any>(`${this.apiUrl}/auth/refresh`, { refreshToken: user.refreshToken })
      .pipe(
        tap((response) => {
          if (response.user) {
            const updatedUser: User = {
              ...response.user,
              expiresAt: Date.now() + (response.expiresIn * 1000)
            };
            this.saveAuthData('user', updatedUser);
            this.currentUserSubject.next(updatedUser);
          }
        }),
        catchError(() => {
          this.logout().subscribe();
          return of(null);
        })
      );
  }

  /**
   * Clear all cookies related to authentication
   */
  private clearAllCookies(): void {
    const expiredDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    const hostname = window.location.hostname;
    const domain = hostname.includes('.') ? `.${hostname.split('.').slice(-2).join('.')}` : hostname;

    // Clear access_token cookie with domain
    document.cookie = `access_token=; expires=${expiredDate}; path=/; domain=${domain};`;
    document.cookie = `access_token=; expires=${expiredDate}; path=/; domain=${domain}; SameSite=None; Secure;`;
    
    // Also try without domain
    document.cookie = `access_token=; expires=${expiredDate}; path=/;`;
    document.cookie = `access_token=; expires=${expiredDate}; path=/; SameSite=None; Secure;`;
  }

  /**
   * Logout user - clears all authentication state and calls logout API
   * @returns Observable that completes when logout is done (or fails)
   */
  logout(): Observable<any> {
    // Clear local state first
    this.currentUserSubject.next(null);
    this.clearAuthData();
    this.stopSessionTimer();
    this.clearAllCookies();
    
    // Clear permission service tokens
    this.permissionService.clearAuthToken();
    
    // Call logout API and return Observable
    // Use catchError to ensure Observable always completes even if API call fails
    return this.http.post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        catchError((error) => {
          // Even if logout API fails, we've already cleared local state
          // Log the error but don't block logout process
          console.warn('[AuthService] Logout API call failed, but local state cleared:', error);
          return of(null);
        })
      );
  }

  private stopSessionTimer(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
      this.refreshTimer = undefined;
    }
  }

  async saveAuthData(key: string, value: any): Promise<void> {
    const db = await this.dbPromise;
    await db.put('auth-store', value, key);
  }

  async clearAuthData(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear('auth-store');
  }

  async getAuthData(key: string): Promise<any> {
    const db = await this.dbPromise;
    return db.get('auth-store', key);
  }

  isAuthenticated(): boolean {
    const user = this.currentUserSubject.value;
    return user !== null && this.isTokenValid(user);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get the current user's role
   * @returns The user's role or null if not logged in
   */
  getCurrentUserRole(): string | null {
    const user = this.currentUserSubject.value;
    return user?.role ?? null;
  }

  /**
   * Save center information to IndexedDB
   */
  async saveCenterInfo(center: CenterInfo): Promise<void> {
    await this.saveAuthData('center', center);
  }

  /**
   * Get center information from IndexedDB
   */
  async getCenterInfo(): Promise<CenterInfo | null> {
    return await this.getAuthData('center');
  }

  /**
   * Clear center information from IndexedDB
   */
  async clearCenterInfo(): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('auth-store', 'center');
  }
}